// Catálogo del POS en Dexie (tabla catalogo, una fila por sucursal). Se guarda TODO o NADA:
// si falla una de las llamadas principales se conserva el caché anterior completo.
import { db } from '@/db/posDb';
import { fetchWrapper } from '@/helpers/fetchWrapper';
import { CATALOGO } from '@/config/offline';
import { useAuth, useConexion } from '@/stores';
import { guardarImagenesCatalogo } from '@/services-offline/imagenesLocal';

const API = process.env.VUE_APP_ROOT_API;
const descargasEnCurso = {};

const respondio = (r) => r.ok && r.data;

export function descargarCatalogo(sucursalId) {
  const id = Number(sucursalId);
  if (!id) return Promise.resolve(null);
  if (descargasEnCurso[id]) return descargasEnCurso[id];
  const t = { timeoutMs: CATALOGO.timeoutMs };
  descargasEnCurso[id] = Promise.all([
    fetchWrapper.raw('GET', `${API}/pos/catalogo?sucursal_id=${id}&todas_las_vigencias=1`, null, t),
    fetchWrapper.raw('GET', `${API}/menu-frontend`, null, t),
    fetchWrapper.raw('GET', `${API}/canal-venta/select`, null, t),
    fetchWrapper.raw('GET', `${API}/producto/bordes?sucursal_id=${id}`, null, t),
    fetchWrapper.raw('GET', `${API}/producto/extras?sucursal_id=${id}`, null, t),
    fetchWrapper.raw('GET', `${API}/caja/verificar`, null, t),
    fetchWrapper.raw('GET', `${API}/empresa`, null, t),
  ]).then(async ([cat, grupos, canales, bordes, extras, caja, empresa]) => {
    if (![cat, grupos, canales, bordes, extras, caja].every(respondio)) return null;
    const auth = useAuth();
    const fila = {
      sucursal_id: id,
      user_id: auth.user ? auth.user.id : null,
      productos: cat.data.data || [],
      // /menu-frontend devuelve el array de grupos directamente
      grupos: grupos.data.grupos || grupos.data.data || (Array.isArray(grupos.data) ? grupos.data : []),
      canales: canales.data.canales_venta || canales.data.canalventa || [],
      bordes: bordes.data.productos || [],
      extras: extras.data.extras || [],
      caja: (caja.data.caja || []).find((c) => Number(c.sucursal_id) === id) || null,
      // El logo no es imprescindible (sin «Sucursal Empresa» /empresa no lo devuelve): se conserva el anterior
      empresa_logo: respondio(empresa) && empresa.data.data ? empresa.data.data.logo || null : await logoAnterior(id),
      // Ítem 20 (opción B de propuesta_ticket_local.md): encabezado del ticket (datos + bytes ESC/POS con logo) que genera el
      // backend. Si esta descarga no lo trae (backend anterior o error al generarlo), se conserva el anterior; sin ninguno, el
      // ticket local se imprime sin encabezado, como antes.
      encabezado_ticket: cat.data.encabezado_ticket || await encabezadoAnterior(id),
      descargado_en: new Date().toISOString(),
      version_app: CATALOGO.versionApp,
    };
    await db.catalogo.put(fila);
    guardarImagenesCatalogo(fila.productos).catch((e) => console.warn('Imágenes del catálogo:', e));
    return fila;
  }).finally(() => { delete descargasEnCurso[id]; });
  return descargasEnCurso[id];
}

async function encabezadoAnterior(id) {
  const anterior = await db.catalogo.get(id);
  return anterior ? anterior.encabezado_ticket || null : null;
}

async function logoAnterior(id) {
  const anterior = await db.catalogo.get(id);
  return anterior ? anterior.empresa_logo : null;
}

export async function leerCatalogo(sucursalId) {
  const fila = await db.catalogo.get(Number(sucursalId));
  return fila && fila.version_app === CATALOGO.versionApp ? fila : null;
}

// Para entrar al POS sin red: la fila cuya caja abierta es del usuario actual
export async function leerCatalogoDeMiCaja() {
  const auth = useAuth();
  const userId = auth.user ? auth.user.id : null;
  const filas = await db.catalogo.toArray();
  return filas.find((f) => f.version_app === CATALOGO.versionApp && f.caja && Number(f.caja.user_id) === Number(userId)
    && Number(f.caja.estado) === 1) || null;
}

export function catalogoVencido(fila) {
  if (!fila) return true;
  return Date.now() - Date.parse(fila.descargado_en) > CATALOGO.horasVigencia * 3600 * 1000;
}

// Al iniciar sesión: si el usuario tiene caja abierta, descarga el catálogo de esa sucursal
// (solo si el caché no existe o tiene más de CATALOGO.horasVigencia horas)
export async function descargarCatalogoDeMiCaja() {
  if (useConexion().modoOffline) return null;
  const r = await fetchWrapper.raw('GET', `${API}/caja/verificar`, null, { timeoutMs: 8000 });
  const caja = r.ok && r.data && r.data.caja && r.data.caja[0];
  if (!caja) return null;
  const fila = await leerCatalogo(caja.sucursal_id);
  return catalogoVencido(fila) ? descargarCatalogo(caja.sucursal_id) : fila;
}

// Etapa 3B: el servidor respondió 409 caja_cerrada. Sin esto, el POS sin red seguiría creyendo que la caja está abierta.
export async function olvidarCajaDelCatalogo(sucursalId) {
  await db.catalogo.update(Number(sucursalId), { caja: null });
}

// Tras cada venta online: solo productos (stock), sin repetir las otras 6 llamadas
export async function refrescarProductos(sucursalId) {
  const id = Number(sucursalId);
  const r = await fetchWrapper.raw('GET', `${API}/pos/catalogo?sucursal_id=${id}&todas_las_vigencias=1`, null, { timeoutMs: CATALOGO.timeoutMs });
  if (!respondio(r)) return null;
  await db.catalogo.update(id, { productos: r.data.data || [] });
  return r.data.data || [];
}
